package com.kanati.admin.features.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.admin.state.formatLocalDateTimeDisplay
import com.kanati.admin.state.formatMoney
import com.kanati.shared.event.EventDto
import com.kanati.shared.report.DailySalesReportRow
import com.kanati.shared.report.InventoryReportRow
import com.kanati.shared.report.TopProductReportRow
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private data class DashboardData(
    val todayRow: DailySalesReportRow?,
    val topProducts: List<TopProductReportRow>,
    val inventoryItems: List<InventoryReportRow>,
    val productCount: Int,
    val eventAttendance: DashboardEventAttendance?,
)

private data class DashboardEventAttendance(
    val event: EventDto,
    val soldCount: Int,
    val checkedInCount: Int,
) {
    val remainingCount: Int
        get() = (soldCount - checkedInCount).coerceAtLeast(0)
}

@Composable
fun DashboardScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    today: String,
    theme: MaterialThemePalette,
    onNavigateToInventory: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    var data by remember { mutableStateOf<DashboardData?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var activeListDialog by remember { mutableStateOf<Pair<String, List<InventoryReportRow>>?>(null) }

    fun missingDistrictMessage(): String = when {
        districtLoading -> "Loading district context\u2026"
        districtLoadError != null -> "District context could not be loaded. Reload context and select a district."
        district == null -> "Select a district to view the game-night dashboard."
        else -> "Select a school to view the game-night dashboard."
    }

    suspend fun refreshDashboard(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isLoading = true
        loadError = null
        data = null
        try {
            coroutineScope {
                val dailySalesDeferred = async {
                    apiClient.getReports(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        from = today,
                        to = today,
                    )
                }
                val topProductsDeferred = async {
                    apiClient.getTopProductsReport(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        from = today,
                        to = today,
                        limit = 5,
                    )
                }
                val inventoryDeferred = async {
                    apiClient.getInventoryOnHand(selectedDistrict.id, selectedSchool.id)
                }
                val productsDeferred = async {
                    apiClient.getProducts(selectedDistrict.id, selectedSchool.id)
                }
                val eventAttendanceDeferred = async {
                    val activeEvent = apiClient.getEvents(selectedDistrict.id, selectedSchool.id)
                        .filter { it.active }
                        .minByOrNull { it.startDate }
                        ?: return@async null
                    val stats = apiClient.getEventStats(selectedDistrict.id, selectedSchool.id, activeEvent.id)
                    DashboardEventAttendance(
                        event = activeEvent,
                        soldCount = stats.soldCount,
                        checkedInCount = stats.usedCount,
                    )
                }

                val dailySales = dailySalesDeferred.await()
                val topProducts = topProductsDeferred.await()
                val inventory = inventoryDeferred.await()
                val products = productsDeferred.await()
                val eventAttendance = eventAttendanceDeferred.await()

                data = DashboardData(
                    todayRow = dailySales.firstOrNull { it.date == today },
                    topProducts = topProducts,
                    inventoryItems = inventory,
                    productCount = products.count { it.active },
                    eventAttendance = eventAttendance,
                )
            }
        } catch (exception: Exception) {
            loadError = "Game-night metrics could not be loaded. Attendance, sales, and stock are unknown. Check access and connectivity, then refresh."
        } finally {
            isLoading = false
        }
    }

    LaunchedEffect(district?.id, school?.id, districtLoading, districtLoadError, today) {
        data = null
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict != null && selectedSchool != null) {
            refreshDashboard(selectedDistrict, selectedSchool)
        } else {
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Game Night Overview",
        theme = theme,
        description = "Real-time snapshot for ${school?.name ?: district?.name ?: "the selected school"} \u2014 ${today}.",
        headerActions = {
            ActionButton(
                label = if (isLoading) "Loading\u2026" else "Refresh",
                onClick = {
                    val selectedDistrict = district
                    val selectedSchool = school
                    if (selectedDistrict != null && selectedSchool != null) {
                        scope.launch { refreshDashboard(selectedDistrict, selectedSchool) }
                    }
                },
                theme = theme,
                isPrimary = false,
                isDisabled = isLoading || district == null || school == null,
            )
        },
    ) {
        if (district == null || school == null) {
            StatusBanner(
                message = missingDistrictMessage(),
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
            return@SectionCard
        }

        loadError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
            return@SectionCard
        }

        val snapshot = data

        EventAttendanceCard(
            attendance = snapshot?.eventAttendance,
            isLoading = isLoading,
            theme = theme,
        )

        // Top metrics row
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Row)
                    gap(12.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            DashboardStatTile(
                label = "Today\u2019s Total",
                value = if (isLoading) "\u2014" else formatMoney(snapshot?.todayRow?.totalAmount ?: 0L),
                supporting = if (snapshot?.todayRow != null)
                    "Subtotal ${formatMoney(snapshot.todayRow.subtotalAmount)} \u00b7 Tax ${formatMoney(snapshot.todayRow.taxAmount)}"
                else if (!isLoading) "No sales recorded yet today"
                else null,
                isEmphasized = true,
                theme = theme,
            )
            DashboardStatTile(
                label = "Active Products",
                value = if (isLoading) "\u2014" else (snapshot?.productCount?.toString() ?: "\u2014"),
                supporting = null,
                theme = theme,
            )
            DashboardStatTile(
                label = "Inventory Items Tracked",
                value = if (isLoading) "\u2014" else (snapshot?.inventoryItems?.size?.toString() ?: "\u2014"),
                supporting = null,
                theme = theme,
            )
        }

        val zeroStockItems = snapshot?.inventoryItems?.filter { it.quantityOnHand == 0 }.orEmpty()
        val lowStockItems = snapshot?.inventoryItems?.filter { it.quantityOnHand in 1..5 }.orEmpty()

        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Row)
                    gap(12.px)
                    property("flex-wrap", "wrap")
                }
            }
        ) {
            DashboardStatTile(
                label = "Out of Stock",
                subLabel = "Restock Before Open",
                value = if (isLoading) "\u2014" else zeroStockItems.size.toString(),
                supporting = if (isLoading) null else formatItemList(zeroStockItems, "out of stock"),
                theme = theme,
                alertTone = if (zeroStockItems.isNotEmpty()) BannerTone.Error else null,
                onClick = if (zeroStockItems.size > 3) { { activeListDialog = "Out of Stock" to zeroStockItems } } else null
            )
            DashboardStatTile(
                label = "Running Low (\u2264 5)",
                subLabel = "Check Before Game Time",
                value = if (isLoading) "\u2014" else lowStockItems.size.toString(),
                supporting = if (isLoading) null else formatItemList(lowStockItems, "running low"),
                theme = theme,
                alertTone = if (lowStockItems.isNotEmpty()) BannerTone.Error else null,
                onClick = if (lowStockItems.size > 3) { { activeListDialog = "Running Low" to lowStockItems } } else null
            )
        }

        // Top sellers today
        if (!isLoading && snapshot != null) {
            if (snapshot.topProducts.isNotEmpty()) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(6.px)
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("font-size", "0.78rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.09em")
                                property("text-transform", "uppercase")
                                property("color", theme.onSurfaceVariant)
                            }
                        }
                    ) {
                        Text("Top Sellers Today")
                    }

                    snapshot.topProducts.forEachIndexed { index, product ->
                        Div(
                            attrs = {
                                style {
                                    display(DisplayStyle.Flex)
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                    property("gap", "12px")
                                    property("flex-wrap", "wrap")
                                    padding(10.px, 14.px)
                                    borderRadius(16.px)
                                    property("background", theme.surfaceContainerHigh)
                                    property("border", "1px solid ${theme.outlineVariant}")
                                }
                            }
                        ) {
                            Div(
                                attrs = {
                                    style {
                                        display(DisplayStyle.Flex)
                                        property("align-items", "center")
                                        gap(10.px)
                                    }
                                }
                            ) {
                                Div(
                                    attrs = {
                                        style {
                                            property("width", "26px")
                                            property("height", "26px")
                                            property("border-radius", "50%")
                                            property("background", theme.primaryContainer)
                                            property("color", theme.onPrimaryContainer)
                                            display(DisplayStyle.Flex)
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                            property("font-size", "0.7rem")
                                            property("font-weight", "700")
                                            property("flex-shrink", "0")
                                        }
                                    }
                                ) { Text("${index + 1}") }

                                Div(
                                    attrs = {
                                        style {
                                            display(DisplayStyle.Flex)
                                            flexDirection(FlexDirection.Column)
                                            gap(2.px)
                                        }
                                    }
                                ) {
                                    P(
                                        attrs = {
                                            style {
                                                margin(0.px)
                                                property("font-weight", "650")
                                                property("font-size", "0.94rem")
                                            }
                                        }
                                    ) { Text(product.productName) }
                                    P(
                                        attrs = {
                                            style {
                                                margin(0.px)
                                                property("font-size", "0.82rem")
                                                property("color", theme.onSurfaceVariant)
                                            }
                                        }
                                    ) { Text("${product.unitsSold} sold") }
                                }
                            }

                            Div(
                                attrs = {
                                    style {
                                        padding(6.px, 12.px)
                                        borderRadius(999.px)
                                        property("background", theme.tertiaryContainer)
                                        property("color", theme.onTertiaryContainer)
                                        property("font-weight", "700")
                                        property("font-size", "0.9rem")
                                    }
                                }
                            ) { Text(formatMoney(product.grossSalesAmount)) }
                        }
                    }
                }
            } else {
                StatusBanner(
                    message = "No sales recorded yet today for ${district.name}. Check back once the first transactions come in.",
                    theme = theme,
                )
            }
        }

        activeListDialog?.let { (title, items) ->
            ItemListDialog(
                title = title,
                items = items,
                onClose = { activeListDialog = null },
                onNavigateToInventory = onNavigateToInventory,
                theme = theme,
            )
        }
    }
}

@Composable
private fun EventAttendanceCard(
    attendance: DashboardEventAttendance?,
    isLoading: Boolean,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                padding(18.px, 20.px)
                borderRadius(24.px)
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "0 10px 24px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.1em")
                    property("text-transform", "uppercase")
                    property("color", theme.onSurfaceVariant)
                }
            }
        ) { Text("Event Attendance") }

        when {
            isLoading && attendance == null -> {
                H3(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "1.5rem")
                            property("letter-spacing", "-0.03em")
                        }
                    }
                ) { Text("Loading event attendance\u2026") }
            }

            attendance == null -> {
                H3(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "1.35rem")
                            property("letter-spacing", "-0.03em")
                        }
                    }
                ) { Text("No active event is tracking attendance yet") }

                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                            property("font-size", "0.92rem")
                            property("line-height", "1.45")
                        }
                    }
                ) {
                    Text("Once an event is active, tickets sold, checked in, and remaining will show here.")
                }
            }

            else -> {
                H3(
                    attrs = {
                        style {
                            margin(0.px)
                            property("font-size", "1.5rem")
                            property("letter-spacing", "-0.03em")
                        }
                    }
                ) { Text(attendance.event.name) }

                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                            property("font-size", "0.9rem")
                            property("line-height", "1.45")
                        }
                    }
                ) {
                    Text("${formatLocalDateTimeDisplay(attendance.event.startDate)} \u00b7 ${attendance.event.location}")
                }

                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Row)
                            gap(12.px)
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    AttendanceMetric(
                        label = "Tickets Sold",
                        value = attendance.soldCount.toString(),
                        theme = theme,
                        tone = "primary",
                    )
                    AttendanceMetric(
                        label = "Checked In",
                        value = attendance.checkedInCount.toString(),
                        theme = theme,
                        tone = "secondary",
                    )
                    AttendanceMetric(
                        label = "Remaining",
                        value = attendance.remainingCount.toString(),
                        theme = theme,
                        tone = "tertiary",
                    )
                }
            }
        }
    }
}

@Composable
private fun AttendanceMetric(
    label: String,
    value: String,
    theme: MaterialThemePalette,
    tone: String,
) {
    val background = when (tone) {
        "primary" -> theme.primaryContainer
        "secondary" -> theme.secondaryContainer
        else -> theme.tertiaryContainer
    }
    val foreground = when (tone) {
        "primary" -> theme.onPrimaryContainer
        "secondary" -> theme.onSecondaryContainer
        else -> theme.onTertiaryContainer
    }

    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                flex(1)
                property("min-width", "150px")
                padding(14.px, 16.px)
                borderRadius(18.px)
                property("background", background)
                property("color", foreground)
                property("border", "1px solid ${theme.outlineVariant}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    property("opacity", "0.85")
                }
            }
        ) { Text(label) }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "1.85rem")
                    property("font-weight", "760")
                    property("letter-spacing", "-0.04em")
                    property("line-height", "1")
                }
            }
        ) { Text(value) }
    }
}

@Composable
private fun DashboardStatTile(
    label: String,
    value: String,
    supporting: String?,
    theme: MaterialThemePalette,
    isEmphasized: Boolean = false,
    alertTone: BannerTone? = null,
    onClick: (() -> Unit)? = null,
    subLabel: String? = null,
) {
    var isPressed by remember(isEmphasized) { mutableStateOf(false) }
    Div(
        attrs = {
            if (onClick != null) {
                onClick { 
                    it.stopPropagation()
                    onClick()
                }
                onMouseDown { isPressed = true }
                onMouseUp { isPressed = false }
                onMouseLeave { isPressed = false }
            }
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                padding(16.px, 18.px)
                borderRadius(22.px)
                flex(1)
                property("min-width", "160px")
                if (onClick != null) {
                    property("cursor", "pointer")
                    property("transition", "transform 0.1s")
                    property("transform", if (isPressed) "scale(0.98)" else "scale(1)")
                    property("user-select", "none")
                }
                property(
                    "background",
                    if (isEmphasized)
                        "linear-gradient(180deg, ${theme.primaryContainer} 0%, ${theme.onPrimaryContainer} 100%)"
                    else if (alertTone == BannerTone.Error)
                        theme.errorContainer
                    else
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                )
                property(
                    "color",
                    if (isEmphasized) theme.onPrimaryContainer 
                    else if (alertTone == BannerTone.Error) theme.onErrorContainer 
                    else theme.onSurface
                )
                property(
                    "border",
                    if (alertTone == BannerTone.Error) "1px solid ${theme.error}" else "1px solid ${theme.outlineVariant}"
                )
                property("box-shadow", "0 8px 20px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", "0.72rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.1em")
                    property("text-transform", "uppercase")
                    property(
                        "color",
                        if (isEmphasized) theme.onPrimaryContainer 
                        else if (alertTone == BannerTone.Error) theme.onErrorContainer 
                        else theme.onSurfaceVariant
                    )
                    property("opacity", if (isEmphasized) "0.9" else "1")
                }
            }
        ) { Text(label) }

        subLabel?.let {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("margin-top", "-2px")
                        property("margin-bottom", "4px")
                        property("font-size", "0.68rem")
                        property("font-weight", "650")
                        property("text-transform", "uppercase")
                        property(
                            "color",
                            if (isEmphasized) theme.onPrimaryContainer 
                            else if (alertTone == BannerTone.Error) theme.onErrorContainer 
                            else theme.onSurfaceVariant
                        )
                        property("opacity", "0.8")
                    }
                }
            ) { Text(it) }
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("font-size", if (isEmphasized) "1.75rem" else "1.3rem")
                    property("font-weight", "750")
                    property("letter-spacing", "-0.03em")
                    property("line-height", "1")
                }
            }
        ) { Text(value) }

        supporting?.let {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.8rem")
                        property(
                            "color",
                            if (isEmphasized) theme.onPrimaryContainer 
                            else if (alertTone == BannerTone.Error) theme.onErrorContainer 
                            else theme.onSurfaceVariant
                        )
                        property("line-height", "1.4")
                        property("opacity", if (isEmphasized) "0.8" else "1")
                    }
                }
            ) { Text(it) }
        }
    }
}

private fun formatItemList(items: List<InventoryReportRow>, actionContext: String): String {
    if (items.isEmpty()) return "No items are $actionContext."
    val count = items.size
    val prefix = "\u26a0\ufe0f "
    return when (count) {
        1 -> "$prefix${items[0].productName} is $actionContext."
        2 -> "$prefix${items[0].productName} and ${items[1].productName} are $actionContext."
        3 -> "$prefix${items[0].productName}, ${items[1].productName}, and ${items[2].productName} are $actionContext."
        else -> "$prefix${items[0].productName}, ${items[1].productName}, ${items[2].productName}, and ${count - 3} more are $actionContext. Click to view all."
    }
}

@Composable
private fun ItemListDialog(
    title: String,
    items: List<InventoryReportRow>,
    onClose: () -> Unit,
    onNavigateToInventory: () -> Unit,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "24px")
                property("background", "rgba(15, 23, 42, 0.24)")
                property("backdrop-filter", "blur(10px)")
                property("z-index", "20")
            }
            onClick { onClose() }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("width", "min(100%, 420px)")
                    property("max-height", "80vh")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "16px")
                    padding(22.px)
                    borderRadius(28.px)
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                    )
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 24px 60px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                }
            }
        ) {
            H3(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                        property("font-size", "1.4rem")
                        property("letter-spacing", "-0.03em")
                    }
                }
            ) {
                Text(title)
            }

            Div(
                attrs = {
                    style {
                        property("overflow-y", "auto")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "8px")
                        property("padding-right", "8px")
                    }
                }
            ) {
                items.forEach { item ->
                    P(
                        attrs = {
                            style {
                                margin(0.px)
                                property("color", theme.onSurface)
                                property("font-size", "0.95rem")
                                property("line-height", "1.4")
                                property("padding", "8px 12px")
                                property("background", theme.surfaceContainer)
                                property("border", "1px solid ${theme.outlineVariant}")
                                borderRadius(8.px)
                            }
                        }
                    ) {
                        Text(item.productName)
                    }
                }
            }

            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "flex-end")
                        gap(12.px)
                        property("margin-top", "8px")
                    }
                }
            ) {
                ActionButton(
                    label = "Close",
                    onClick = onClose,
                    theme = theme,
                    isPrimary = false,
                )
                ActionButton(
                    label = "Go to Inventory",
                    onClick = {
                        onClose()
                        onNavigateToInventory()
                    },
                    theme = theme,
                    isPrimary = true,
                )
            }
        }
    }
}
